import React, { useMemo, useState } from 'react';

function PerformanceChart({ data, bets }) {
    const [hoveredPoint, setHoveredPoint] = useState(null);
    const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
    const [chartType, setChartType] = useState('line'); // 'line' or 'candlestick'

    const chartDimensions = {
        width: 800,
        height: 300,
        padding: { top: 40, right: 60, bottom: 50, left: 70 }
    };

    // Calculate daily P/L data for candlestick chart
    const dailyData = useMemo(() => {
        if (!bets || bets.length === 0) return [];
        
        const dailyMap = {};
        const sortedBets = [...bets].sort((a, b) => new Date(a.date) - new Date(b.date));
        
        sortedBets.forEach(bet => {
            if (!dailyMap[bet.date]) {
                dailyMap[bet.date] = {
                    date: bet.date,
                    bets: [],
                    open: 0,
                    close: 0,
                    high: 0,
                    low: 0,
                    pl: 0
                };
            }
            dailyMap[bet.date].bets.push(bet);
            dailyMap[bet.date].pl += bet.pl;
        });

        // Calculate cumulative and OHLC values
        let cumulative = 0;
        const dailyArray = Object.values(dailyMap).sort((a, b) => new Date(a.date) - new Date(b.date));
        
        dailyArray.forEach(day => {
            day.open = cumulative;
            
            // Simulate intraday movement based on individual bets
            let runningPL = cumulative;
            let high = cumulative;
            let low = cumulative;
            
            day.bets.forEach(bet => {
                runningPL += bet.pl;
                high = Math.max(high, runningPL);
                low = Math.min(low, runningPL);
            });
            
            day.close = cumulative + day.pl;
            day.high = high;
            day.low = low;
            cumulative = day.close;
        });

        return dailyArray;
    }, [bets]);

    const { pathData, areaPath, points, yAxisLabels, xAxisLabels, minValue, maxValue, zeroY } = useMemo(() => {
        if (data.length === 0) {
            return { pathData: '', areaPath: '', points: [], yAxisLabels: [], xAxisLabels: [], minValue: 0, maxValue: 0, zeroY: 0 };
        }

        const { width, height, padding } = chartDimensions;
        const chartWidth = width - padding.left - padding.right;
        const chartHeight = height - padding.top - padding.bottom;

        // Helper function to get nice round numbers for axis
        const getNiceNumber = (value, round) => {
            const exponent = Math.floor(Math.log10(Math.abs(value) || 1));
            const fraction = value / Math.pow(10, exponent);
            let niceFraction;
            
            if (round) {
                if (fraction < 1.5) niceFraction = 1;
                else if (fraction < 3) niceFraction = 2;
                else if (fraction < 7) niceFraction = 5;
                else niceFraction = 10;
            } else {
                if (fraction <= 1) niceFraction = 1;
                else if (fraction <= 2) niceFraction = 2;
                else if (fraction <= 5) niceFraction = 5;
                else niceFraction = 10;
            }
            
            return niceFraction * Math.pow(10, exponent);
        };

        // Calculate min/max with nice rounding
        const values = data.map(d => d.cumulative);
        const rawMin = Math.min(0, ...values);
        const rawMax = Math.max(0, ...values);
        const valueRange = rawMax - rawMin || 100;
        
        // Calculate nice tick spacing
        const tickSpacing = getNiceNumber(valueRange / 5, true);
        
        // Round min/max to nice values
        const minVal = Math.floor(rawMin / tickSpacing) * tickSpacing;
        const maxVal = Math.ceil(rawMax / tickSpacing) * tickSpacing;

        // Calculate points
        const pts = data.map((d, i) => {
            const x = padding.left + (i / (data.length - 1 || 1)) * chartWidth;
            const y = padding.top + chartHeight - ((d.cumulative - minVal) / (maxVal - minVal)) * chartHeight;
            return { x, y, ...d };
        });

        // Generate smooth curve path using Catmull-Rom spline
        const generateSmoothPath = (points) => {
            if (points.length < 2) return '';
            
            const tension = 0.3;
            let path = `M ${points[0].x} ${points[0].y}`;
            
            for (let i = 0; i < points.length - 1; i++) {
                const p0 = points[Math.max(0, i - 1)];
                const p1 = points[i];
                const p2 = points[i + 1];
                const p3 = points[Math.min(points.length - 1, i + 2)];
                
                const cp1x = p1.x + (p2.x - p0.x) * tension;
                const cp1y = p1.y + (p2.y - p0.y) * tension;
                const cp2x = p2.x - (p3.x - p1.x) * tension;
                const cp2y = p2.y - (p3.y - p1.y) * tension;
                
                path += ` C ${cp1x} ${cp1y}, ${cp2x} ${cp2y}, ${p2.x} ${p2.y}`;
            }
            
            return path;
        };

        const linePath = generateSmoothPath(pts);
        
        // Area path (fill under the curve to zero line)
        const zeroLineY = padding.top + chartHeight - ((0 - minVal) / (maxVal - minVal)) * chartHeight;
        const area = `${linePath} L ${pts[pts.length - 1].x} ${zeroLineY} L ${pts[0].x} ${zeroLineY} Z`;

        // Generate Y-axis labels with nice round numbers
        const yLabels = [];
        for (let value = maxVal; value >= minVal; value -= tickSpacing) {
            const y = padding.top + chartHeight - ((value - minVal) / (maxVal - minVal)) * chartHeight;
            yLabels.push({ value: Math.round(value), y });
        }

        // Generate X-axis labels (dates)
        const xLabels = [];
        const numXLabels = Math.min(6, data.length);
        for (let i = 0; i < numXLabels; i++) {
            const index = Math.floor((i / (numXLabels - 1 || 1)) * (data.length - 1));
            const x = pts[index]?.x || 0;
            const date = data[index]?.date || '';
            xLabels.push({ date, x });
        }

        return {
            pathData: linePath,
            areaPath: area,
            points: pts,
            yAxisLabels: yLabels,
            xAxisLabels: xLabels,
            minValue: minVal,
            maxValue: maxVal,
            zeroY: zeroLineY
        };
    }, [data]);

    // Candlestick chart calculations
    const candlestickData = useMemo(() => {
        if (dailyData.length === 0) {
            return { candles: [], yAxisLabels: [], xAxisLabels: [], minValue: 0, maxValue: 0, zeroY: 0, tickSpacing: 1000 };
        }

        const { width, height, padding } = chartDimensions;
        const chartWidth = width - padding.left - padding.right;
        const chartHeight = height - padding.top - padding.bottom;

        // Helper function to get nice round numbers for axis
        const getNiceNumber = (value, round) => {
            const exponent = Math.floor(Math.log10(Math.abs(value) || 1));
            const fraction = value / Math.pow(10, exponent);
            let niceFraction;
            
            if (round) {
                if (fraction < 1.5) niceFraction = 1;
                else if (fraction < 3) niceFraction = 2;
                else if (fraction < 7) niceFraction = 5;
                else niceFraction = 10;
            } else {
                if (fraction <= 1) niceFraction = 1;
                else if (fraction <= 2) niceFraction = 2;
                else if (fraction <= 5) niceFraction = 5;
                else niceFraction = 10;
            }
            
            return niceFraction * Math.pow(10, exponent);
        };

        // Calculate min/max with nice rounding
        const allValues = dailyData.flatMap(d => [d.high, d.low, d.open, d.close]);
        const rawMin = Math.min(0, ...allValues);
        const rawMax = Math.max(0, ...allValues);
        const valueRange = rawMax - rawMin || 100;
        
        // Calculate nice tick spacing
        const tickSpacing = getNiceNumber(valueRange / 5, true);
        
        // Round min/max to nice values
        const minVal = Math.floor(rawMin / tickSpacing) * tickSpacing;
        const maxVal = Math.ceil(rawMax / tickSpacing) * tickSpacing;

        const candleWidth = Math.max(8, Math.min(30, (chartWidth / dailyData.length) * 0.6));
        const gap = (chartWidth - (candleWidth * dailyData.length)) / (dailyData.length + 1);

        const candles = dailyData.map((d, i) => {
            const x = padding.left + gap + i * (candleWidth + gap) + candleWidth / 2;
            const scaleY = (val) => padding.top + chartHeight - ((val - minVal) / (maxVal - minVal)) * chartHeight;
            
            return {
                ...d,
                x,
                candleWidth,
                openY: scaleY(d.open),
                closeY: scaleY(d.close),
                highY: scaleY(d.high),
                lowY: scaleY(d.low),
                isGreen: d.close >= d.open
            };
        });

        const zeroLineY = padding.top + chartHeight - ((0 - minVal) / (maxVal - minVal)) * chartHeight;

        // Y-axis labels with nice round numbers
        const yLabels = [];
        for (let value = maxVal; value >= minVal; value -= tickSpacing) {
            const y = padding.top + chartHeight - ((value - minVal) / (maxVal - minVal)) * chartHeight;
            yLabels.push({ value: Math.round(value), y });
        }

        // X-axis labels
        const xLabels = [];
        const numXLabels = Math.min(6, dailyData.length);
        for (let i = 0; i < numXLabels; i++) {
            const index = Math.floor((i / (numXLabels - 1 || 1)) * (dailyData.length - 1));
            const candle = candles[index];
            if (candle) {
                xLabels.push({ date: candle.date, x: candle.x });
            }
        }

        return {
            candles,
            yAxisLabels: yLabels,
            xAxisLabels: xLabels,
            minValue: minVal,
            maxValue: maxVal,
            zeroY: zeroLineY
        };
    }, [dailyData]);

    const handleMouseMove = (e, point) => {
        const rect = e.currentTarget.getBoundingClientRect();
        setTooltipPosition({ x: e.clientX - rect.left, y: e.clientY - rect.top });
        setHoveredPoint(point);
    };

    const formatCurrency = (value) => {
        const prefix = value >= 0 ? '+' : '';
        return `${prefix}$${Math.abs(value).toFixed(2)}`;
    };

    const formatDate = (dateStr) => {
        const date = new Date(dateStr);
        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    };

    const lastValue = data.length > 0 ? data[data.length - 1].cumulative : 0;
    const isPositive = lastValue >= 0;

    return (
        <div className="bg-slate-900/50 rounded-xl md:rounded-2xl border border-slate-700/50 p-2 md:p-6 overflow-hidden">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4 md:mb-6">
                <div className="flex items-center justify-between sm:block">
                    <div>
                        <h3 className="text-base md:text-lg font-semibold text-white mb-0.5 md:mb-1">P/L Performance</h3>
                        <p className="text-xs md:text-sm text-slate-400 hidden sm:block">Cumulative profit/loss over time</p>
                    </div>
                    {/* P/L value - shown inline on mobile */}
                    <div className={`text-xl md:text-2xl font-bold sm:hidden ${isPositive ? 'text-emerald-400' : 'text-red-400'}`}>
                        {formatCurrency(lastValue)}
                    </div>
                </div>
                <div className="flex items-center justify-between sm:justify-end gap-2 md:gap-4">
                    {/* Chart Type Toggle */}
                    <div className="flex bg-slate-800/50 rounded-lg p-1 border border-slate-700/50">
                        <button
                            onClick={() => setChartType('line')}
                            className={`px-2 md:px-3 py-1 md:py-1.5 rounded-md text-xs font-medium transition-all duration-200 flex items-center gap-1 md:gap-1.5 ${
                                chartType === 'line'
                                    ? 'bg-gradient-to-r from-emerald-600 to-teal-600 text-white'
                                    : 'text-slate-400 hover:text-white'
                            }`}
                        >
                            <svg className="w-3.5 h-3.5 md:w-4 md:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 12l3-3 3 3 4-4M8 21l4-4 4 4M3 4h18M4 4h16v12a1 1 0 01-1 1H5a1 1 0 01-1-1V4z" />
                            </svg>
                            <span className="hidden xs:inline">Line</span>
                        </button>
                        <button
                            onClick={() => setChartType('candlestick')}
                            className={`px-2 md:px-3 py-1 md:py-1.5 rounded-md text-xs font-medium transition-all duration-200 flex items-center gap-1 md:gap-1.5 ${
                                chartType === 'candlestick'
                                    ? 'bg-gradient-to-r from-emerald-600 to-teal-600 text-white'
                                    : 'text-slate-400 hover:text-white'
                            }`}
                        >
                            <svg className="w-3.5 h-3.5 md:w-4 md:h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 4v4m0 4v8M9 8h.01M9 12h.01M15 4v8m0 4v4M15 12h.01M15 16h.01" />
                                <rect x="7" y="8" width="4" height="4" strokeWidth="2" fill="currentColor" opacity="0.3" />
                                <rect x="13" y="12" width="4" height="4" strokeWidth="2" fill="currentColor" opacity="0.3" />
                            </svg>
                            <span className="hidden xs:inline">Candles</span>
                        </button>
                    </div>
                    {/* P/L value - hidden on mobile, shown on sm+ */}
                    <div className={`hidden sm:block text-xl md:text-2xl font-bold ${isPositive ? 'text-emerald-400' : 'text-red-400'}`}>
                        {formatCurrency(lastValue)}
                    </div>
                </div>
            </div>

            {/* Chart - extends to card edges on mobile, capped on ultrawide screens */}
            <div className="relative w-auto -mx-2 md:mx-0 2xl:max-w-[90%] 2xl:mx-auto">
                <svg 
                    viewBox={`0 0 ${chartDimensions.width} ${chartDimensions.height}`}
                    className="w-full h-auto"
                >
                    <defs>
                        {/* Gradient for positive area */}
                        <linearGradient id="positiveGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                            <stop offset="0%" stopColor="#10b981" stopOpacity="0.3" />
                            <stop offset="100%" stopColor="#10b981" stopOpacity="0" />
                        </linearGradient>
                        
                        {/* Gradient for negative area */}
                        <linearGradient id="negativeGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                            <stop offset="0%" stopColor="#ef4444" stopOpacity="0" />
                            <stop offset="100%" stopColor="#ef4444" stopOpacity="0.3" />
                        </linearGradient>

                        {/* Line gradient */}
                        <linearGradient id="lineGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                            <stop offset="0%" stopColor="#10b981" />
                            <stop offset="100%" stopColor="#14b8a6" />
                        </linearGradient>

                        {/* Glow filter */}
                        <filter id="glow" x="-50%" y="-50%" width="200%" height="200%">
                            <feGaussianBlur stdDeviation="2" result="coloredBlur" />
                            <feMerge>
                                <feMergeNode in="coloredBlur" />
                                <feMergeNode in="SourceGraphic" />
                            </feMerge>
                        </filter>

                        {/* Candlestick glow */}
                        <filter id="candleGlow" x="-50%" y="-50%" width="200%" height="200%">
                            <feGaussianBlur stdDeviation="1.5" result="coloredBlur" />
                            <feMerge>
                                <feMergeNode in="coloredBlur" />
                                <feMergeNode in="SourceGraphic" />
                            </feMerge>
                        </filter>
                    </defs>

                    {chartType === 'line' ? (
                        <>
                            {/* Grid lines */}
                            {yAxisLabels.map((label, i) => (
                                <line
                                    key={i}
                                    x1={chartDimensions.padding.left}
                                    y1={label.y}
                                    x2={chartDimensions.width - chartDimensions.padding.right}
                                    y2={label.y}
                                    stroke="#334155"
                                    strokeWidth="1"
                                    strokeDasharray={label.value === 0 ? "none" : "4,4"}
                                    opacity={label.value === 0 ? 0.8 : 0.3}
                                />
                            ))}

                            {/* Zero line highlight */}
                            <line
                                x1={chartDimensions.padding.left}
                                y1={zeroY}
                                x2={chartDimensions.width - chartDimensions.padding.right}
                                y2={zeroY}
                                stroke="#64748b"
                                strokeWidth="1"
                            />

                            {/* Area fill */}
                            <path
                                d={areaPath}
                                fill={isPositive ? "url(#positiveGradient)" : "url(#negativeGradient)"}
                                opacity="0.5"
                            />

                            {/* Main line */}
                            <path
                                d={pathData}
                                fill="none"
                                stroke={isPositive ? "url(#lineGradient)" : "#ef4444"}
                                strokeWidth="3"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                filter="url(#glow)"
                            />

                            {/* Y-axis labels */}
                            {yAxisLabels.map((label, i) => (
                                <text
                                    key={i}
                                    x={chartDimensions.padding.left - 12}
                                    y={label.y + 4}
                                    fill="#64748b"
                                    fontSize="11"
                                    textAnchor="end"
                                    fontFamily="Inter, sans-serif"
                                >
                                    ${label.value.toFixed(0)}
                                </text>
                            ))}

                            {/* X-axis labels */}
                            {xAxisLabels.map((label, i) => (
                                <text
                                    key={i}
                                    x={label.x}
                                    y={chartDimensions.height - chartDimensions.padding.bottom + 25}
                                    fill="#64748b"
                                    fontSize="11"
                                    textAnchor="middle"
                                    fontFamily="Inter, sans-serif"
                                >
                                    {formatDate(label.date)}
                                </text>
                            ))}

                            {/* Invisible hover area for the entire chart */}
                            <rect
                                x={chartDimensions.padding.left}
                                y={chartDimensions.padding.top}
                                width={chartDimensions.width - chartDimensions.padding.left - chartDimensions.padding.right}
                                height={chartDimensions.height - chartDimensions.padding.top - chartDimensions.padding.bottom}
                                fill="transparent"
                                onMouseMove={(e) => {
                                    const rect = e.currentTarget.getBoundingClientRect();
                                    const mouseX = e.clientX - rect.left;
                                    const chartWidth = chartDimensions.width - chartDimensions.padding.left - chartDimensions.padding.right;
                                    const relativeX = mouseX / rect.width * chartWidth;
                                    
                                    // Find the closest point to the mouse position
                                    let closestPoint = points[0];
                                    let minDist = Infinity;
                                    points.forEach(point => {
                                        const pointRelativeX = point.x - chartDimensions.padding.left;
                                        const dist = Math.abs(pointRelativeX - relativeX);
                                        if (dist < minDist) {
                                            minDist = dist;
                                            closestPoint = point;
                                        }
                                    });
                                    
                                    if (closestPoint) {
                                        handleMouseMove(e, closestPoint);
                                    }
                                }}
                                onMouseLeave={() => setHoveredPoint(null)}
                                style={{ cursor: 'crosshair' }}
                            />
                            
                            {/* Hover indicator line and point */}
                            {hoveredPoint && (
                                <>
                                    {/* Vertical line */}
                                    <line
                                        x1={hoveredPoint.x}
                                        y1={chartDimensions.padding.top}
                                        x2={hoveredPoint.x}
                                        y2={chartDimensions.height - chartDimensions.padding.bottom}
                                        stroke="#64748b"
                                        strokeWidth="1"
                                        strokeDasharray="4,4"
                                        opacity="0.5"
                                    />
                                    {/* Point indicator */}
                                    <circle
                                        cx={hoveredPoint.x}
                                        cy={hoveredPoint.y}
                                        r="6"
                                        fill={hoveredPoint.cumulative >= 0 ? "#10b981" : "#ef4444"}
                                        stroke="#020817"
                                        strokeWidth="2"
                                        style={{ 
                                            filter: 'drop-shadow(0 0 8px rgba(16, 185, 129, 0.5))'
                                        }}
                                    />
                                </>
                            )}
                        </>
                    ) : (
                        <>
                            {/* Candlestick Chart */}
                            {/* Grid lines */}
                            {candlestickData.yAxisLabels.map((label, i) => (
                                <line
                                    key={i}
                                    x1={chartDimensions.padding.left}
                                    y1={label.y}
                                    x2={chartDimensions.width - chartDimensions.padding.right}
                                    y2={label.y}
                                    stroke="#334155"
                                    strokeWidth="1"
                                    strokeDasharray={Math.abs(label.value) < 0.01 ? "none" : "4,4"}
                                    opacity={Math.abs(label.value) < 0.01 ? 0.8 : 0.3}
                                />
                            ))}

                            {/* Zero line */}
                            <line
                                x1={chartDimensions.padding.left}
                                y1={candlestickData.zeroY}
                                x2={chartDimensions.width - chartDimensions.padding.right}
                                y2={candlestickData.zeroY}
                                stroke="#64748b"
                                strokeWidth="1"
                            />

                            {/* Candlesticks */}
                            {candlestickData.candles.map((candle, i) => {
                                const isHovered = hoveredPoint?.date === candle.date;
                                const bodyTop = Math.min(candle.openY, candle.closeY);
                                const bodyHeight = Math.abs(candle.closeY - candle.openY) || 2;
                                
                                return (
                                    <g 
                                        key={i}
                                        onMouseEnter={(e) => handleMouseMove(e, candle)}
                                        onMouseMove={(e) => handleMouseMove(e, candle)}
                                        onMouseLeave={() => setHoveredPoint(null)}
                                        style={{ cursor: 'pointer' }}
                                    >
                                        {/* Wick (high-low line) */}
                                        <line
                                            x1={candle.x}
                                            y1={candle.highY}
                                            x2={candle.x}
                                            y2={candle.lowY}
                                            stroke={candle.isGreen ? "#10b981" : "#ef4444"}
                                            strokeWidth={isHovered ? 2 : 1.5}
                                            filter={isHovered ? "url(#candleGlow)" : "none"}
                                        />
                                        
                                        {/* Body */}
                                        <rect
                                            x={candle.x - candle.candleWidth / 2}
                                            y={bodyTop}
                                            width={candle.candleWidth}
                                            height={bodyHeight}
                                            fill={candle.isGreen ? "#10b981" : "#ef4444"}
                                            stroke={candle.isGreen ? "#059669" : "#dc2626"}
                                            strokeWidth={isHovered ? 2 : 1}
                                            rx="2"
                                            filter={isHovered ? "url(#candleGlow)" : "none"}
                                            className="transition-all duration-150"
                                        />
                                        
                                        {/* Gradient overlay for depth */}
                                        <rect
                                            x={candle.x - candle.candleWidth / 2}
                                            y={bodyTop}
                                            width={candle.candleWidth}
                                            height={bodyHeight}
                                            fill="url(#candleShine)"
                                            rx="2"
                                            opacity="0.3"
                                        />
                                    </g>
                                );
                            })}

                            {/* Y-axis labels */}
                            {candlestickData.yAxisLabels.map((label, i) => (
                                <text
                                    key={i}
                                    x={chartDimensions.padding.left - 12}
                                    y={label.y + 4}
                                    fill="#64748b"
                                    fontSize="11"
                                    textAnchor="end"
                                    fontFamily="Inter, sans-serif"
                                >
                                    ${label.value.toFixed(0)}
                                </text>
                            ))}

                            {/* X-axis labels */}
                            {candlestickData.xAxisLabels.map((label, i) => (
                                <text
                                    key={i}
                                    x={label.x}
                                    y={chartDimensions.height - chartDimensions.padding.bottom + 25}
                                    fill="#64748b"
                                    fontSize="11"
                                    textAnchor="middle"
                                    fontFamily="Inter, sans-serif"
                                >
                                    {formatDate(label.date)}
                                </text>
                            ))}
                        </>
                    )}
                </svg>

                {/* Tooltip */}
                {hoveredPoint && (
                    <div 
                        className="absolute pointer-events-none bg-slate-800 border border-slate-600 rounded-lg p-3 shadow-xl z-10"
                        style={{
                            left: Math.min(tooltipPosition.x + 10, chartDimensions.width - 200),
                            top: tooltipPosition.y - 80,
                            transform: 'translateX(-50%)'
                        }}
                    >
                        <div className="text-xs text-slate-400 mb-1">{formatDate(hoveredPoint.date)}</div>
                        {chartType === 'line' ? (
                            <>
                                <div className="text-sm text-white font-medium mb-1 truncate max-w-[180px]">
                                    {hoveredPoint.game}
                                </div>
                                <div className="text-xs text-slate-300 mb-2 truncate max-w-[180px]">
                                    {hoveredPoint.bet}
                                </div>
                                <div className="flex items-center gap-3">
                                    <div>
                                        <div className="text-xs text-slate-500">Bet P/L</div>
                                        <div className={`text-sm font-semibold ${hoveredPoint.pl >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                                            {formatCurrency(hoveredPoint.pl)}
                                        </div>
                                    </div>
                                    <div>
                                        <div className="text-xs text-slate-500">Cumulative</div>
                                        <div className={`text-sm font-semibold ${hoveredPoint.cumulative >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                                            {formatCurrency(hoveredPoint.cumulative)}
                                        </div>
                                    </div>
                                </div>
                            </>
                        ) : (
                            <>
                                <div className="text-sm text-white font-medium mb-2">
                                    {hoveredPoint.bets?.length || 0} bet{hoveredPoint.bets?.length !== 1 ? 's' : ''} placed
                                </div>
                                <div className="grid grid-cols-2 gap-2 text-xs">
                                    <div>
                                        <span className="text-slate-500">Open:</span>
                                        <span className="text-white ml-1">{formatCurrency(hoveredPoint.open)}</span>
                                    </div>
                                    <div>
                                        <span className="text-slate-500">Close:</span>
                                        <span className={`ml-1 ${hoveredPoint.close >= hoveredPoint.open ? 'text-emerald-400' : 'text-red-400'}`}>
                                            {formatCurrency(hoveredPoint.close)}
                                        </span>
                                    </div>
                                    <div>
                                        <span className="text-slate-500">High:</span>
                                        <span className="text-emerald-400 ml-1">{formatCurrency(hoveredPoint.high)}</span>
                                    </div>
                                    <div>
                                        <span className="text-slate-500">Low:</span>
                                        <span className="text-red-400 ml-1">{formatCurrency(hoveredPoint.low)}</span>
                                    </div>
                                </div>
                                <div className="mt-2 pt-2 border-t border-slate-700">
                                    <span className="text-slate-500 text-xs">Day P/L:</span>
                                    <span className={`ml-1 font-semibold ${hoveredPoint.pl >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                                        {formatCurrency(hoveredPoint.pl)}
                                    </span>
                                </div>
                            </>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}

export default PerformanceChart;
